import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import DossierDocuments from "../components/DossierDocuments";
import CollaborateurStats from "../components/CollaborateurStats";
import CollaborateurAbsencesHistory from "../components/CollaborateurAbsencesHistory";
import PurgeCollabEquipmentDialog from "../components/PurgeCollabEquipmentDialog";
import PurgeCollabSiteRequestsDialog from "../components/PurgeCollabSiteRequestsDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { ChevronLeft, BarChart3, FolderOpen, Trash2, XCircle, Wallet } from "lucide-react";
import StickyPageHeader from "../components/StickyPageHeader";
import RemunerationCard from "../components/RemunerationCard";

export default function DossierCollaborateur() {
  const { id } = useParams();
  const { user } = useAuth();
  const [collab, setCollab] = useState(null);
  const [isSalarie, setIsSalarie] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeSiteReqOpen, setPurgeSiteReqOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: collabs }, { data: cats }] = await Promise.all([
          api.get("/collaborateurs"),
          api.get("/categories"),
        ]);
        const found = (collabs || []).find((c) => c.id === id);
        setCollab(found);
        const cat = (cats || []).find((c) => c.id === found?.category_id);
        setIsSalarie(!!cat?.is_salarie);
      } catch {
        // ignore
      }
    })();
  }, [id]);

  const canManage = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";
  // Règle métier : l'onglet « Rémunération » (barème, %CA, primes site) ne
  // concerne QUE les collaborateurs non-salariés. À l'inverse, la sous-section
  // « Relevé de rémunération » (bulletins de salaire dans Documents) est
  // réservée aux salariés — piloté via la prop `isSalarie` de DossierDocuments.
  const showRemunTab = isAdmin && !isSalarie;

  return (
    <div className="space-y-6" data-testid="dossier-collab-page">
      {collab ? (
        <Tabs defaultValue="recap" className="w-full">
          <StickyPageHeader testid="dossier-collab-sticky-header">
          <Link to="/collaborateurs" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900" data-testid="back-to-collabs">
            <ChevronLeft size={14} /> Retour aux collaborateurs
          </Link>

          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="label-uppercase mb-2">Dossier collaborateur</p>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
                {collab?.name || "—"}
              </h1>
              <p className="text-sm text-zinc-600 mt-1 font-mono">{collab?.email}{collab?.poste ? ` · ${collab.poste}` : ""}</p>
            </div>
            {isAdmin && collab && (
              <div className="flex flex-wrap gap-2 pt-6">
                <Button
                  variant="outline"
                  onClick={() => setPurgeOpen(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="collab-purge-equipment-btn"
                  title="Purger l'historique matériel de ce collaborateur"
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Purger l&apos;historique matériel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPurgeSiteReqOpen(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="collab-purge-site-requests-btn"
                  title="Purger l'historique des demandes pour sites de ce collaborateur"
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Purger les demandes pour sites
                </Button>
              </div>
            )}
          </div>

          <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="w-max">
              <TabsTrigger value="recap" data-testid="tab-recap"><BarChart3 size={14} className="mr-1.5" /> Récapitulatif</TabsTrigger>
              <TabsTrigger value="absences" data-testid="tab-absences"><XCircle size={14} className="mr-1.5" /> Absences du collaborateur</TabsTrigger>
              <TabsTrigger value="docs" data-testid="tab-docs"><FolderOpen size={14} className="mr-1.5" /> Documents</TabsTrigger>
              {showRemunTab && (
                <TabsTrigger value="remuneration" data-testid="tab-remuneration"><Wallet size={14} className="mr-1.5" /> Rémunération</TabsTrigger>
              )}
            </TabsList>
          </div>
          </StickyPageHeader>

          <TabsContent value="recap">
            <CollaborateurStats collaborateurId={collab.id} />
          </TabsContent>
          <TabsContent value="absences">
            <CollaborateurAbsencesHistory collaborateurId={collab.id} />
          </TabsContent>
          <TabsContent value="docs">
            <DossierDocuments
              collaborateurId={collab.id}
              canUpload={canManage}
              canDelete={canManage}
              viewerRole={user?.role}
              isSalarie={isSalarie}
            />
          </TabsContent>
          {showRemunTab && (
            <TabsContent value="remuneration">
              <RemunerationCard collaborateur={collab} onSaved={(updated) => setCollab(updated)} />
            </TabsContent>
          )}
        </Tabs>
      ) : null}

      {isAdmin && collab && (
        <PurgeCollabEquipmentDialog
          open={purgeOpen}
          onOpenChange={setPurgeOpen}
          collaborateur={collab}
          onDone={() => {
            // Rien à recharger dans cette page (les stats n'incluent pas le matériel).
          }}
        />
      )}

      {isAdmin && collab && (
        <PurgeCollabSiteRequestsDialog
          open={purgeSiteReqOpen}
          onOpenChange={setPurgeSiteReqOpen}
          collaborateur={collab}
          onDone={() => {
            // Rien à recharger dans cette page (les stats n'incluent pas les demandes site).
          }}
        />
      )}
    </div>
  );
}
